import { ref, computed, type Ref, type ComputedRef } from "vue";
import {
  adminV1OrganizationsPaymentsLogStatisticsRetrieve,
  type AdminV1OrganizationsPaymentsLogStatisticsRetrieveResult,
} from "@/api/admin-paymentslog-statistics/admin-paymentslog-statistics";
import { getFormattedCurrency } from "@/shared/utils/helpers";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import type {
  PaymentsLogStatistics,
  AdminV1OrganizationsPaymentsLogStatisticsRetrieveParams,
} from "@/api/model";

const EmptyStatistics: Partial<PaymentsLogStatistics> = {
  total_revenue: undefined,
  total_customers: undefined,
  average_payment_value: undefined,
  total_payments_count: undefined,
  outstanding_total: undefined,
  outstanding_invoices: undefined,
  outstanding_registrations: undefined,
  outstanding_memberships: undefined,
  scheduled_revenue: undefined,
  scheduled_payments_count: undefined,
  past_due_revenue: undefined,
  past_due_payments_count: undefined,
};

export function usePaymentsLogPaypointStatistics(): {
  fetchPaymentsLogStatistics: (
    params?: AdminV1OrganizationsPaymentsLogStatisticsRetrieveParams
  ) => Promise<void>;
  statisticsData: Ref<Partial<PaymentsLogStatistics>>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
} {
  const statisticsData = ref<Partial<PaymentsLogStatistics>>(EmptyStatistics);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const getDefaultOrganizationId = (): number => {
    const { currentOrganizationId } = useCurrentOrganization();
    const orgId = currentOrganizationId.value;
    if (!orgId) {
      throw new Error("No default organization found");
    }
    return orgId;
  };
  const fetchPaymentsLogStatistics = async (
    params?: AdminV1OrganizationsPaymentsLogStatisticsRetrieveParams
  ) => {
    const organizationId = getDefaultOrganizationId();
    isLoading.value = true;
    error.value = null;

    try {
      const result = await adminV1OrganizationsPaymentsLogStatisticsRetrieve(
        organizationId,
        params
      );
      statisticsData.value = result;
    } catch (err: any) {
      error.value = err.message || "Failed to fetch payments data";
      statisticsData.value = EmptyStatistics;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    fetchPaymentsLogStatistics,
    statisticsData,
    isLoading,
    error,
  };
}
